<template>
  <div class="forgot-password-container">
    <div class="forgot-password-card">
      <!-- Header Section -->
      <div class="header-section">
        <div class="logo-icon">🔐</div>
        <h1>Mot de passe oublié</h1>
        <p class="subtitle">
          {{ emailSent ? 'Email envoyé avec succès' : 'Entrez votre email pour réinitialiser votre mot de passe' }}
        </p>
      </div>

      <!-- Success State -->
      <div v-if="emailSent" class="success-state">
        <div class="success-icon-container">
          <div class="success-checkmark">✓</div>
        </div>
        <p class="success-message">
          Un email de réinitialisation a été envoyé à<br>
          <strong>{{ email }}</strong>
        </p>
        <div class="instructions-box">
          <p class="instruction-title">Instructions :</p>
          <ol class="instruction-list">
            <li>Consultez votre boîte de réception</li>
            <li>Cliquez sur le lien dans l'email</li>
            <li>Créez votre nouveau mot de passe</li>
          </ol>
        </div>
        <p class="help-text">
          💡 Vérifiez vos spams si vous ne trouvez pas l'email
        </p>

        <!-- Resend Button -->
        <div class="resend-section">
          <p class="resend-text">Vous n'avez pas reçu l'email ?</p>
          <button
            v-if="resendCooldown === 0"
            @click="resendEmail"
            class="btn btn-outline"
            :disabled="resending"
          >
            <span v-if="resending">Envoi en cours...</span>
            <span v-else>Renvoyer l'email</span>
          </button>
          <span v-else class="cooldown-text">
            Renvoyer dans {{ resendCooldown }}s
          </span>
        </div>

        <!-- Back to Login -->
        <button @click="goToLogin" class="btn btn-secondary">
          <span class="btn-icon">←</span>
          Retour à la connexion
        </button>
      </div>

      <!-- Form State -->
      <div v-else>
        <!-- Error Message -->
        <div v-if="errorMessage" class="error-banner">
          <span class="error-icon">⚠</span>
          <span>{{ errorMessage }}</span>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="forgot-password-form">
          <div class="form-group">
            <label for="email">Email professionnel</label>
            <input
              id="email"
              v-model="email"
              type="email"
              placeholder="jean.dupont@exemple.fr"
              required
              :disabled="loading"
              autofocus
            />
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading">Envoi en cours...</span>
            <span v-else>Envoyer le lien de réinitialisation</span>
          </button>
        </form>

        <!-- Back to Login -->
        <div class="back-link">
          <button @click="goToLogin" class="btn-text">
            <span class="btn-icon">←</span>
            Retour à la connexion
          </button>
        </div>
      </div>

      <!-- Footer Info -->
      <div class="footer-info">
        <p>
          Le lien de réinitialisation est valide pendant 1 heure.
        </p>
      </div>
    </div>

    <!-- Background decoration -->
    <div class="background-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      email: '',
      loading: false,
      emailSent: false,
      errorMessage: '',
      resending: false,
      resendCooldown: 0,
      resendTimer: null,
    }
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }
  },
  methods: {
    async handleSubmit() {
      if (this.loading) return

      this.loading = true
      this.errorMessage = ''

      try {
        // Appeler l'API de réinitialisation de mot de passe
        await AuthService.requestPasswordReset(this.email)

        // Afficher l'état de succès
        this.emailSent = true

        // Démarrer le cooldown de 60 secondes
        this.startCooldown()
      } catch (error) {
        console.error('Erreur lors de la demande de réinitialisation:', error)

        // Gérer les erreurs
        if (error.response?.status === 404) {
          this.errorMessage = 'Aucun compte trouvé avec cet email.'
        } else if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            this.errorMessage = error.response.data.detail
          } else {
            this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.'
          }
        } else {
          this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.'
        }
      } finally {
        this.loading = false
      }
    },

    async resendEmail() {
      if (this.resendCooldown > 0 || this.resending) return

      this.resending = true
      this.errorMessage = ''

      try {
        await AuthService.requestPasswordReset(this.email)

        // Redémarrer le cooldown
        this.startCooldown()
      } catch (error) {
        console.error('Erreur lors du renvoi de l\'email:', error)
        this.errorMessage = 'Impossible de renvoyer l\'email. Veuillez réessayer.'
      } finally {
        this.resending = false
      }
    },

    startCooldown() {
      this.resendCooldown = 60
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown === 0) {
          clearInterval(this.resendTimer)
        }
      }, 1000)
    },

    goToLogin() {
      this.$router.push({ name: 'AccompanistLogin' })
    },
  },
}
</script>

<style scoped>
/* Container principal */
.forgot-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  position: relative;
  overflow: hidden;
}

/* Background decoration */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -100px;
  right: -100px;
  animation-delay: 5s;
}

.circle-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  right: -75px;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* Card */
.forgot-password-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  position: relative;
  z-index: 1;
  animation: slideUp 0.6s ease;
  max-height: calc(100vh - 20px);
  overflow-y: auto;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 20px;
}

.logo-icon {
  font-size: 44px;
  margin-bottom: 10px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.header-section h1 {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.subtitle {
  font-size: 13px;
  color: #546e7a;
  margin: 0;
  line-height: 1.4;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-left: 3px solid #f44336;
  border-radius: 8px;
  color: #c62828;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 14px;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-icon {
  font-size: 18px;
}

/* Form */
.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  margin-left: 4px;
}

.form-group input {
  padding: 10px 12px;
  border: 2px solid #e0e7ee;
  border-radius: 10px;
  font-size: 13px;
  transition: all 0.3s ease;
  background-color: #fafbfc;
}

.form-group input:focus {
  outline: none;
  border-color: #FF9800;
  background-color: white;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1);
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Buttons */
.btn {
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #FF9800;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.btn-secondary {
  background: white;
  color: #FF9800;
  border: 2px solid #FF9800;
  margin-top: 12px;
  width: 100%;
}

.btn-secondary:hover {
  background: #FF9800;
  color: white;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: #FF9800;
  border: 2px solid #FF9800;
  font-size: 14px;
  padding: 10px 20px;
}

.btn-outline:hover:not(:disabled) {
  background: #FF9800;
  color: white;
  transform: translateY(-2px);
}

.btn-icon {
  font-size: 18px;
}

/* Back Link */
.back-link {
  text-align: center;
  margin-top: 16px;
}

.btn-text {
  background: none;
  border: none;
  color: #FF9800;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  background: rgba(255, 152, 0, 0.1);
  color: #FF9800;
}

/* Success State */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.success-icon-container {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-checkmark {
  font-size: 42px;
  color: white;
  font-weight: bold;
}

.success-message {
  text-align: center;
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.5;
  margin: 0;
}

.success-message strong {
  color: #FF9800;
  font-weight: 700;
}

/* Instructions Box */
.instructions-box {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 10px;
  border-left: 4px solid #FF9800;
}

.instruction-title {
  font-size: 13px;
  font-weight: 700;
  color: #FF9800;
  margin: 0 0 8px 0;
}

.instruction-list {
  margin: 0;
  padding-left: 18px;
  color: #2c3e50;
  font-size: 13px;
  line-height: 1.6;
}

.instruction-list li {
  margin-bottom: 5px;
}

.help-text {
  font-size: 12px;
  color: #90a4ae;
  text-align: center;
  margin: 0;
  padding: 10px;
  background: rgba(255, 152, 0, 0.1);
  border-radius: 8px;
  width: 100%;
}

/* Resend Section */
.resend-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.resend-text {
  font-size: 14px;
  color: #546e7a;
  margin: 0;
}

.cooldown-text {
  font-size: 14px;
  color: #90a4ae;
  font-weight: 600;
}

/* Footer Info */
.footer-info {
  margin-top: 16px;
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid #e0e7ee;
}

.footer-info p {
  font-size: 11px;
  color: #90a4ae;
  line-height: 1.4;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .forgot-password-card {
    padding: 24px 20px;
  }

  .header-section h1 {
    font-size: 22px;
  }

  .logo-icon {
    font-size: 48px;
  }

  .btn {
    width: 100%;
  }
}

/* Media query pour les écrans avec hauteur réduite */
@media (max-height: 700px) {
  .forgot-password-container {
    padding: 6px;
  }

  .forgot-password-card {
    padding: 18px;
    max-height: calc(100vh - 16px);
  }

  .header-section {
    margin-bottom: 14px;
  }

  .logo-icon {
    font-size: 38px;
    margin-bottom: 7px;
  }

  .header-section h1 {
    font-size: 20px;
    margin-bottom: 4px;
  }

  .subtitle {
    font-size: 12px;
  }

  .forgot-password-form {
    gap: 14px;
    margin-bottom: 14px;
  }

  .form-group {
    gap: 4px;
  }

  .form-group label {
    font-size: 11px;
  }

  .form-group input {
    padding: 9px 11px;
    font-size: 12px;
  }

  .btn {
    padding: 9px 16px;
    font-size: 13px;
  }

  .success-state {
    gap: 14px;
  }

  .success-icon-container {
    width: 60px;
    height: 60px;
  }

  .success-checkmark {
    font-size: 36px;
  }

  .success-message {
    font-size: 13px;
  }

  .instructions-box {
    padding: 12px;
  }

  .instruction-title {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .instruction-list {
    font-size: 12px;
    line-height: 1.5;
    padding-left: 16px;
  }

  .instruction-list li {
    margin-bottom: 4px;
  }

  .help-text {
    font-size: 11px;
    padding: 8px;
  }

  .footer-info {
    margin-top: 14px;
    padding-top: 10px;
  }

  .footer-info p {
    font-size: 10px;
  }

  .error-banner {
    padding: 7px 10px;
    font-size: 11px;
    margin-bottom: 12px;
  }

  .back-link {
    margin-top: 12px;
  }

  .btn-text {
    font-size: 13px;
    padding: 7px 10px;
  }
}

/* Media query pour les très petits écrans en hauteur */
@media (max-height: 600px) {
  .forgot-password-container {
    padding: 4px;
  }

  .forgot-password-card {
    padding: 14px;
    max-height: calc(100vh - 12px);
    border-radius: 14px;
  }

  .header-section {
    margin-bottom: 10px;
  }

  .logo-icon {
    font-size: 32px;
    margin-bottom: 5px;
  }

  .header-section h1 {
    font-size: 18px;
    margin-bottom: 3px;
  }

  .subtitle {
    font-size: 11px;
    line-height: 1.3;
  }

  .forgot-password-form {
    gap: 12px;
    margin-bottom: 10px;
  }

  .form-group {
    gap: 3px;
  }

  .form-group label {
    font-size: 10px;
  }

  .form-group input {
    padding: 7px 9px;
    font-size: 11px;
    border-radius: 8px;
  }

  .btn {
    padding: 7px 14px;
    font-size: 12px;
    border-radius: 8px;
  }

  .success-state {
    gap: 10px;
  }

  .success-icon-container {
    width: 50px;
    height: 50px;
  }

  .success-checkmark {
    font-size: 30px;
  }

  .success-message {
    font-size: 12px;
    line-height: 1.4;
  }

  .instructions-box {
    padding: 10px;
    border-radius: 8px;
  }

  .instruction-title {
    font-size: 11px;
    margin-bottom: 5px;
  }

  .instruction-list {
    font-size: 11px;
    line-height: 1.4;
    padding-left: 14px;
  }

  .instruction-list li {
    margin-bottom: 3px;
  }

  .help-text {
    font-size: 10px;
    padding: 7px;
  }

  .resend-section {
    gap: 8px;
  }

  .resend-text {
    font-size: 12px;
  }

  .cooldown-text {
    font-size: 12px;
  }

  .btn-secondary {
    margin-top: 8px;
  }

  .footer-info {
    margin-top: 12px;
    padding-top: 8px;
  }

  .footer-info p {
    font-size: 10px;
  }

  .error-banner {
    padding: 6px 9px;
    font-size: 10px;
    margin-bottom: 10px;
  }

  .error-icon {
    font-size: 16px;
  }

  .back-link {
    margin-top: 10px;
  }

  .btn-text {
    font-size: 12px;
    padding: 6px 8px;
  }

  .btn-icon {
    font-size: 16px;
  }
}
</style>
